import { Box, HStack, Text } from "@chakra-ui/react";
import React from "react";
import { HiOutlineCheck, HiOutlineX } from "react-icons/hi";

import { Button } from "../../../../components";
import { SuggestionFieldTarget } from "./types";

type AssistantFieldPreviewProps = {
  target: SuggestionFieldTarget;
  originalValue: string;
  proposedValue: string;
  isDisabled?: boolean;
  onResolve?: (
    target: SuggestionFieldTarget,
    decision: "accept" | "reject"
  ) => void;
};

type DiffPart = {
  type: "unchanged" | "removed" | "added";
  text: string;
};

const RenderDiff: React.FC<{ parts: DiffPart[] }> = ({ parts }) => (
  <>
    {parts.map((part, idx) => (
      <Text
        as="span"
        // eslint-disable-next-line react/no-array-index-key
        key={idx}
        textDecoration={part.type === "removed" ? "line-through" : undefined}
        color={
          part.type === "removed"
            ? "#E44435"
            : part.type === "added"
            ? "#259B71"
            : undefined
        }
      >
        {part.text}
      </Text>
    ))}
  </>
);

const AssistantFieldPreview: React.FC<AssistantFieldPreviewProps> = ({
  target,
  originalValue,
  proposedValue,
  isDisabled,
  onResolve,
}) => {
  const diffParts = computeWordDiff(originalValue, proposedValue);

  return (
    <Box>
      <Box
        p={2}
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        borderRadius="md"
      >
        <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
          {proposedValue ? <RenderDiff parts={diffParts} /> : "No value"}
        </Text>
      </Box>
      <HStack mt={2} spacing={3} flexWrap="wrap">
        <Button
          size="prismSm"
          variant="prismSecondary"
          colorScheme="green"
          minW="auto"
          leftIcon={<HiOutlineCheck size={16} />}
          isDisabled={isDisabled}
          onClick={() => onResolve?.(target, "accept")}
        >
          Accept
        </Button>
        <Button
          size="prismSm"
          variant="prismSecondary"
          colorScheme="red"
          minW="auto"
          color="red.400"
          borderColor="red.400"
          leftIcon={<HiOutlineX size={16} />}
          isDisabled={isDisabled}
          onClick={() => onResolve?.(target, "reject")}
        >
          Ignore
        </Button>
      </HStack>
    </Box>
  );
};

function computeWordDiff(oldText: string, newText: string): DiffPart[] {
  const oldWords = tokenize(oldText);
  const newWords = tokenize(newText);

  const lcs = longestCommonSubsequence(oldWords, newWords);
  const rawParts: DiffPart[] = [];

  let oldIdx = 0;
  let newIdx = 0;
  let lcsIdx = 0;

  while (oldIdx < oldWords.length || newIdx < newWords.length) {
    const removedWords: string[] = [];
    while (
      oldIdx < oldWords.length &&
      (lcsIdx >= lcs.length || oldWords[oldIdx] !== lcs[lcsIdx])
    ) {
      removedWords.push(oldWords[oldIdx]);
      oldIdx += 1;
    }

    const addedWords: string[] = [];
    while (
      newIdx < newWords.length &&
      (lcsIdx >= lcs.length || newWords[newIdx] !== lcs[lcsIdx])
    ) {
      addedWords.push(newWords[newIdx]);
      newIdx += 1;
    }

    if (removedWords.length > 0) {
      rawParts.push({ type: "removed", text: removedWords.join(" ") });
    }
    if (addedWords.length > 0) {
      rawParts.push({ type: "added", text: addedWords.join(" ") });
    }

    if (lcsIdx < lcs.length) {
      rawParts.push({ type: "unchanged", text: lcs[lcsIdx] });
      oldIdx += 1;
      newIdx += 1;
      lcsIdx += 1;
    }
  }

  return mergeAndSpaceParts(rawParts);
}

function mergeAndSpaceParts(parts: DiffPart[]): DiffPart[] {
  if (parts.length === 0) {
    return [];
  }

  const result: DiffPart[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    const prev = result[result.length - 1];

    if (prev && prev.type === part.type) {
      prev.text += ` ${part.text}`;
    } else if (prev) {
      if (prev.type === "unchanged") {
        prev.text += " ";
        result.push(part);
      } else if (part.type === "unchanged") {
        part.text = ` ${part.text}`;
        result.push(part);
      } else {
        result.push({ type: "unchanged", text: " " });
        result.push(part);
      }
    } else {
      result.push(part);
    }
  }

  return result;
}

function tokenize(text: string): string[] {
  return text.split(/\s+/).filter((word) => word.length > 0);
}

function longestCommonSubsequence(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const result: string[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift(a[i - 1]);
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i -= 1;
    } else {
      j -= 1;
    }
  }

  return result;
}

export default AssistantFieldPreview;
